<template>
    <ModalIndex
        v-model:visible="showUpdateModal"
        body-class="update-emd-modal"
        :event-to-close-function="clearUpdateRef"
        footer-class="justify-end"
        fullscreen-on-mobile
        title="Update EMD"
    >
        <p
            v-if="error"
            class="text-danger rounded-md bg-ds-red-100 px-6 py-4 text-base"
        >
            There was an error updating this record. Please try again, or contact support if you continue to experience the
            problem.
        </p>

        <form
            class="grid grid-cols-[1fr_3fr] gap-y-4"
            @submit.prevent="submitUpdate"
        >
            <span class="pt-2">Property Address</span>
            <input
                v-model="payloadFields.address"
                class="form-control p-3"
                name="address"
                required
            />

            <span class="pt-2">Deposit Amount</span>
            <AutoNumericInput
                v-model.cents="payloadFields.deposit_amount!"
                class="form-control p-3"
                name="deposit_amount"
                required
            />

            <input
                class="hidden"
                :disabled="isMissingField"
                type="submit"
            />
            <!-- allows user to submit with 'enter' -->
        </form>

        <template #footer>
            <button
                class="rounded-md bg-ds-primary-900 px-6 py-4 text-white"
                :class="
                    disabledCss('bg-ds-primary-300', 'opacity-75', 'hover:bg-ds-primary-300', 'cursor-not-allowed', 'shadow-none')
                "
                :disabled="isMissingField"
                @click="submitUpdate"
            >
                Confirm
            </button>
            <button
                class="rounded-md px-6 py-4"
                @click="clearUpdateRef"
            >
                Cancel
            </button>
        </template>
    </ModalIndex>
</template>

<script setup lang="ts">
import type { EarlyDeposit } from '@/api/interfaces/early-deposit.types';
import AutoNumericInput from '@/components/Components/AutoNumericInput.vue';
import ModalIndex from '@/components/Components/Modal/ModalIndex.vue';
import { useUpdateEarlyDeposit } from '@/services/early-deposit.service';
import { computed, reactive, watch } from 'vue';

const showUpdateModal = computed<boolean>({
    get: () => !!updateEmdRef.value,
    set: flag => {
        !flag && clearUpdateRef();
    },
});
const emit = defineEmits<{
    emdUpdated: [updatedEmd: EarlyDeposit];
}>();
const payloadFields: Partial<EarlyDeposit> = reactive({});
const isMissingField = computed<boolean>(() => !payloadFields.id || !payloadFields.address || !payloadFields.deposit_amount);

const { runUpdate, clearUpdateRef, updateEmdRef, data: emdResponseRef, error } = useUpdateEarlyDeposit();

const submitUpdate = async () => {
    await runUpdate(payloadFields);
    if (error.value) return;
    emit('emdUpdated', emdResponseRef.value!);
    clearUpdateRef();
};

const disabledCss = (...classes: string[]): string => classes.reduce((acc, css) => `${acc} disabled:${css}`, '');

watch(updateEmdRef, rowData => {
    // sets initial values for <input> fields
    Object.assign(payloadFields, rowData);
});
</script>

<style scoped>
.update-emd-modal input {
    line-height: 1;
}
</style>
